import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Crown, Play } from "lucide-react";
import { api } from "../lib/api";
import useSeo from "../lib/useSeo";

export default function ComicDetailPage() {
    const { slug } = useParams();
    const [comic, setComic] = useState(null);

    useSeo({
        title: comic?.title || "Volume",
        description: comic
            ? `${comic.tagline} — ${comic.synopsis}`.slice(0, 300)
            : "EIGOO Books animated comic — chapter index and volume overview.",
        image: comic?.cover_url,
    });

    useEffect(() => {
        api.get(`/comics/${slug}`).then(({ data }) => setComic(data));
    }, [slug]);

    if (!comic)
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="eyebrow text-[#D4AF37] animate-pulse">Unsealing volume…</div>
            </div>
        );

    return (
        <main data-testid="comic-detail-page" className="text-white">
            <section className="relative overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center scale-110 blur-md"
                    style={{ backgroundImage: `url(${comic.cover_url})`, opacity: 0.35 }}
                />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(10,10,10,0.6) 0%, rgba(10,10,10,0.95) 100%)",
                    }}
                />

                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-12 gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9 }}
                        className="md:col-span-5 md:col-start-1"
                    >
                        <div className="frame-gold p-1 max-w-xs mx-auto md:mx-0">
                            <div
                                className="w-full bg-cover bg-center"
                                style={{
                                    aspectRatio: "3/4",
                                    backgroundImage: `url(${comic.cover_url})`,
                                }}
                            />
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.15 }}
                        className="md:col-span-7"
                    >
                        {comic.is_flagship && (
                            <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#D4AF37]/40 mb-6">
                                <Crown className="w-3.5 h-3.5 text-[#D4AF37]" />
                                <span className="eyebrow text-[10px]">EIGOO Flagship</span>
                            </div>
                        )}
                        <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-[0.95]">
                            {comic.title}
                        </h1>
                        <p className="font-serif italic text-2xl text-[#E6D59A] mt-4">
                            {comic.tagline}
                        </p>
                        <p className="text-[#cfcfcf] mt-6 max-w-xl leading-relaxed">
                            {comic.synopsis}
                        </p>

                        {comic.chapters?.[0] && (
                            <div className="mt-10 flex flex-wrap gap-4">
                                <Link
                                    data-testid="detail-begin-btn"
                                    to={`/read/${comic.chapters[0].id}`}
                                    className="btn-gold"
                                >
                                    <Play className="w-3.5 h-3.5" /> Begin Reading
                                </Link>
                                <a
                                    href="#chapters"
                                    className="btn-ghost-gold"
                                >
                                    All chapters <ArrowRight className="w-3.5 h-3.5" />
                                </a>
                            </div>
                        )}
                    </motion.div>
                </div>
            </section>

            <section
                id="chapters"
                data-testid="chapters-list"
                className="max-w-5xl mx-auto px-6 lg:px-10 py-24"
            >
                <div className="eyebrow mb-3">Volume Index</div>
                <h2 className="font-serif text-4xl mb-12">Chapters</h2>

                {comic.chapters?.length === 0 ? (
                    <p className="text-[#A1A1AA] italic">
                        Chapters for this title arrive in the coming season.
                    </p>
                ) : (
                    <div className="space-y-1">
                        {comic.chapters.map((ch, i) => (
                            <motion.div
                                key={ch.id}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.07 }}
                            >
                                <Link
                                    data-testid={`chapter-row-${ch.order}`}
                                    to={`/read/${ch.id}`}
                                    className="flex items-center justify-between py-6 border-b group transition-colors"
                                    style={{ borderColor: "rgba(212,175,55,0.15)" }}
                                >
                                    <div className="flex items-center gap-6">
                                        <span className="font-serif text-3xl text-[#D4AF37] w-16 group-hover:translate-x-1 transition-transform">
                                            {String(ch.order).padStart(2, "0")}
                                        </span>
                                        <div>
                                            <div className="font-serif text-2xl text-white group-hover:text-[#E6D59A] transition-colors">
                                                {ch.title}
                                            </div>
                                            <div className="eyebrow text-[10px] mt-1 flex items-center gap-2">
                                                <BookOpen className="w-3 h-3" /> Animated chapter
                                            </div>
                                        </div>
                                    </div>
                                    <ArrowRight className="w-5 h-5 text-[#D4AF37] group-hover:translate-x-2 transition-transform" />
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                )}
            </section>
        </main>
    );
}
